import React from 'react';
import {useNavigate} from 'react-router';

import {InfiniteCarousel} from 'src/components';
import {TChannelType, TItemId} from 'src/constants';

import {createHash} from '../../Channels/useAllowedChannelTypes';
import {showReminderModal, useCheckCompanyDetailsFilled} from '../../CompanyDetails';
import {SellingTariffCard} from '../TariffCard';
import {
  useGetAvailableActiveTariffsQuery,
  useGetAvailableTariffsQuery,
  useGetConnectedTariffsQuery,
} from '../api';
import {TAvailableTariff} from '../definitions';
import {getLinkedSubjects} from '../utils';

export const SellingTariffsList = React.memo<{
  tenantId?: TItemId;
  channelType?: TChannelType;
  noTariffsNode?: React.ReactNode;
  showConnectedTariffs?: boolean;
}>(({tenantId, channelType, noTariffsNode, showConnectedTariffs}) => {
  const navigate = useNavigate();
  const isCompanyDetailsFilled = useCheckCompanyDetailsFilled();

  const {
    data: activeTariffs = [],
    isLoading: isLoadingActiveTariffs,
    fulfilledTimeStamp: activeTariffsTimeStamp,
  } = useGetAvailableActiveTariffsQuery(channelType, {
    skip: !channelType || !!tenantId,
  });

  const {
    data: availableTariffs = [],
    isLoading: isLoadingAvailableTariffs,
    fulfilledTimeStamp: availableTariffsTimeStamp,
  } = useGetAvailableTariffsQuery(
    {channelType, tenantId},
    {
      skip: !tenantId,
    },
  );

  const tariffs = React.useMemo(() => {
    if (!tenantId) {
      return activeTariffs;
    }

    return availableTariffs.reduce<TAvailableTariff[]>(
      (result, item) => (item.active ? [...result, item.active] : result),
      [],
    );
  }, [availableTariffsTimeStamp, activeTariffsTimeStamp]);

  const {data: connectedTariffs = [], fulfilledTimeStamp: connectedTariffsTimeStamp} =
    useGetConnectedTariffsQuery(tenantId, {
      skip: !showConnectedTariffs,
    });

  const linkedSubjects = React.useMemo(
    () => getLinkedSubjects(connectedTariffs),
    [connectedTariffsTimeStamp],
  );

  const handleConnect = React.useCallback(
    (tariff: TAvailableTariff) => {
      if (!isCompanyDetailsFilled) {
        showReminderModal();

        return;
      }

      const path = `${createHash[tariff.channelType]}?${new URLSearchParams({tariffId: String(tariff.id)})}`;

      navigate(path);
    },
    [navigate, isCompanyDetailsFilled],
  );

  if (isLoadingActiveTariffs || isLoadingAvailableTariffs) {
    return null;
  }

  if (!tariffs.length && noTariffsNode) {
    return <>{noTariffsNode}</>;
  }

  if (tariffs.length === 1) {
    const tariff = tariffs[0];

    return (
      <SellingTariffCard
        gap={20}
        tariff={tariff}
        key={tariff.id}
        count={tariffs.length}
        onConnect={() => handleConnect(tariff)}
        linkedSubjects={linkedSubjects[tariff.id]}
        readOnly={!!tenantId}
      />
    );
  }

  return (
    <InfiniteCarousel gap={20}>
      {tariffs.map((tariff) => (
        <SellingTariffCard
          gap={20}
          tariff={tariff}
          key={tariff.id}
          count={tariffs.length}
          onConnect={() => handleConnect(tariff)}
          linkedSubjects={linkedSubjects[tariff.id]}
          readOnly={!!tenantId}
        />
      ))}
    </InfiniteCarousel>
  );
});

SellingTariffsList.displayName = 'SellingTariffsList';
